// Drift: Freemi på Supabase (afløser testversionens DemoApi trin for trin). Alle regler håndhæves i databasen (RLS og
// funktioner i supabase/migrations) og i edge-funktionerne – appen viser kun og sender videre.
// Denne fil: Freemis hjemmeside, tilmelding, login, Freemi-konsollen og abonnementet. Katalog, priser, ledige tider
// og opsætning ligger i sbKatalog.ts. Dele, der ikke er koblet på endnu, svarer med en tydelig besked i stedet for at vise forkerte data.
import { createClient, type SupabaseClient, type User } from '@supabase/supabase-js';
import { ApiFejl, KUN_FREEMI, type AbonBetaling, type AbonnementVisning, type Api, type Bruger, type KlientOffentlig, type KlientOversigt, type PlatformBruger, type SmsKoeb, type StagingNulstillet } from './api';
import { live, startSupabaseLive } from './live';
import { PIXIE_BRAND, MODULER, rensSlug, tjekBrand, type Klient, type KlientBrand } from '../../core/klient';
import { abonnementStatus, naesteTraek, PRIS } from '../../core/abonnement';
import { KOM_I_GANG_MANUELLE, komIGang, slugFejl, tilmeldingsBrand, tjekTilmelding, type Tilmelding } from '../../core/tilmelding';
import { deaktiveretLogin } from '../../core/deaktiver';
import { iDag } from '../../core/dato';
import { SALDO, erPakke, inkluderet, maanedsRegnskab } from '../../core/forbrug';
import { RESERVEREDE } from '../../core/ramme';
import type { DriftStatus, FejlGruppe, FejlKilde, FejlRapport } from '../../core/fejlspor';
import type { LatepointSynk } from '../../core/latepointSynk';
import { VILKAAR_VERSION } from '../../core/juridisk';
import { GRAENSER, offentligHjemmeside, tjekAnmeldelse, tjekHenvendelse, tjekReference, type Anmeldelse, type AnmeldelseStatus, type NyAnmeldelse, type NyHenvendelse, type Reference } from '../../core/hjemmeside';
import { KONSOL_VAERT, PIXIE_VAERT, STAGING, paaKonsol } from '../pixieVaert';
import type { StagingRolle } from '../../core/staging';
import { SbKatalog, type SbKontekst } from './sbKatalog';
import { SbMedlemmer } from './sbMedlemmer';
import { SbKontor } from './sbKontor';
import { SbProfil } from './sbProfil';
import { SbChat } from './sbChat';
import { SbSteamer } from './sbSteamer';
import { SbKunde } from './sbKunde';
import { SbRykker } from './sbRykker';
import { SbAnsoeg } from './sbAnsoeg';
import { SbAutomation } from './sbAutomation';
import { SbAnmeld } from './sbAnmeld';
import { SbKundeservice } from './sbKundeservice';
import { SbKundecenter } from './sbKundecenter';
import { SbUdlaeg } from './sbUdlaeg';
import { SbCrm } from './sbCrm';
import { SbKort } from './sbKort';
import { SbMedarbejder } from './sbMedarbejder';
import { SbBrugere } from './sbBrugere';
import { SbBetalinger } from './sbBetalinger';
import { SbIdag } from './sbIdag';
import { SbMarketing } from './sbMarketing';
import { SbAnalyse } from './sbAnalyse';

const INTERNE = new Set(['saaKatalog', 'forKunde', 'kald', 'mineSkabeloner', 'tjekInvitation', 'glemKatalog', 'katalogCache']);   // delenes egne hjælpere – ikke en del af Api
const IKKE_KLAR = 'Denne del af Freemi kobles på databasen i næste trin. Brug testversionen indtil da.';
const fejl = (b: string, f: Record<string, string> = {}) => new ApiFejl(b, f);

/** Database- og loginfejl → en besked på dansk. Funktionernes egne beskeder (raise exception) er allerede på dansk. */
export function oversaetFejl(e: unknown): ApiFejl {
  if (e instanceof ApiFejl) return e;
  const x = e as { message?: string; code?: string; status?: number; name?: string } | null;
  const m = x?.message ?? '';
  if (x?.code === 'invalid_credentials' || /Invalid login credentials/i.test(m)) return fejl('Forkert e-mail eller adgangskode.');
  if (x?.code === 'email_not_confirmed' || /Email not confirmed/i.test(m)) return fejl('Bekræft din e-mail først – tjek din indbakke.');
  if (x?.code === 'user_already_exists' || /already registered/i.test(m)) return fejl('Der findes allerede en konto med den e-mail. Log ind i stedet.');
  if (x?.code === 'weak_password' || /Password should be/i.test(m)) return fejl('Adgangskoden er for svag. Brug mindst 8 tegn.');
  if (x?.code === 'over_email_send_rate_limit' || x?.status === 429) return fejl('Der er sendt mange mails på kort tid. Prøv igen om lidt.');
  if (x?.name === 'TypeError' || /Failed to fetch|NetworkError|Load failed/i.test(m)) return fejl('Freemi kan ikke nås lige nu. Tjek din forbindelse, og prøv igen.');
  if (x?.code === '42501' || /permission denied|row-level security/i.test(m)) return fejl(/^[A-ZÆØÅ]/.test(m) && !/permission/i.test(m) ? m : 'Du har ikke adgang til det.');
  if (m && /^[A-ZÆØÅ][^\n]{2,200}$/.test(m) && /[æøåÆØÅ]|\.\s*$/.test(m)) return fejl(m);   // dansk besked fra databasen
  return fejl('Noget gik galt hos Freemi. Prøv igen – og kontakt os, hvis det bliver ved.');
}

/** Hvilken klient viser adressen? Eget domæne, eller Freemis domæne + navn (freemi.dk/firma). null = Freemis egen side. */
export function klientFraAdresse(host: string, sti: string, vaert = PIXIE_VAERT, konsol = KONSOL_VAERT): { vaert: string; slug: string | null } | null {
  const h = host.toLowerCase().replace(/:\d+$/, '');
  if (konsol && h === konsol) return null;   // Freemi-konsollen (admin.freemi.dk) er ikke en virksomhed
  if (h !== vaert && h !== 'localhost') return { vaert: h, slug: null };
  const del = sti.split('/').filter(Boolean)[0]?.toLowerCase() ?? '';
  return del && /^[a-z0-9][a-z0-9-]{1,39}$/.test(del) && !RESERVEREDE.includes(del) ? { vaert: h, slug: del } : null;
}

type KlientRk = {
  id: string; slug: string; navn: string; domaene: string | null; hjemmeside: string; brand: KlientBrand; moduler: Klient['moduler']; status: Klient['status'];
  oprettet: string; proeve_slut: string | null; kilde: 'pixie' | 'selv'; vilkaar_version: string | null; vilkaar_tid: string | null; domaene_anmodet: string | null;
  kort: Klient['kort']; betaling: Klient['betaling']; opsagt_til: string | null; sms_auto: boolean; sms_loft: number; sms_saldo: number; kom_i_gang: string[];
};
const tilKlient = (r: KlientRk): Klient => ({ ...r, domaene: r.domaene ?? '', brand: { ...PIXIE_BRAND, ...r.brand } });
const PIXIE_SELV: KlientOffentlig = { slug: 'pixie', navn: 'Freemi', domaene: '', hjemmeside: '', brand: PIXIE_BRAND, moduler: MODULER.map((m) => m.id), proeve_slut: null, abon: 'aktiv' };

class SupabaseApi {
  readonly sb: SupabaseClient;
  private lyttere = new Set<() => void>();
  private b: Bruger | null = null;
  private p: PlatformBruger | null = null;
  private adr: { vaert: string; slug: string | null } | null;
  private kid: Promise<string | null> | null = null;
  /** Link fra en login-mail (?token_hash=…&type=…): veksles til et login, før siden bruger det. */
  private readonly klar: Promise<void>;
  private linkFejl: string | null = null;
  /** Siden er åbnet fra et login-link (konsollen logger ind med det, se platformLogIndMedLink). */
  private fraLink = false;
  readonly katalogDel: SbKatalog;
  readonly medlemDel: SbMedlemmer;
  readonly kontorDel: SbKontor;
  readonly profilDel: SbProfil;
  readonly chatDel: SbChat;
  readonly steamerDel: SbSteamer;
  readonly kundeDel: SbKunde;
  readonly rykkerDel: SbRykker;
  readonly ansoegDel: SbAnsoeg;
  readonly automationDel: SbAutomation;
  readonly anmeldDel: SbAnmeld;
  readonly kundecenterDel: SbKundecenter;
  readonly kundeserviceDel: SbKundeservice;
  readonly udlaegDel: SbUdlaeg;
  readonly crmDel: SbCrm;
  readonly kortDel: SbKort;
  readonly medarbejderDel: SbMedarbejder;
  readonly brugerDel: SbBrugere;
  readonly betalingDel: SbBetalinger;
  readonly idagDel: SbIdag;
  readonly marketingDel: SbMarketing;
  readonly analyseDel: SbAnalyse;
  private katalogTjekket = false;

  constructor(url: string, noegle: string) {
    this.sb = createClient(url, noegle, { auth: { flowType: 'pkce', persistSession: true, autoRefreshToken: true, detectSessionInUrl: true } });
    this.adr = typeof location === 'undefined' ? null : klientFraAdresse(location.hostname, location.pathname);
    const q = typeof location === 'undefined' ? new URLSearchParams() : new URLSearchParams(location.search);
    const k = q.get('k');
    if (k && /^[a-z0-9][a-z0-9-]{1,39}$/.test(k) && !this.adr) this.adr = { vaert: PIXIE_VAERT, slug: k };   // login-link til en virksomhed på Freemis domæne
    this.klar = this.vekslLink(q);
    // Appen åbnet fra hjemmeskærmen på Freemis egen adresse, og man er allerede logget ind: videre til sin virksomhed
    if (q.get('app') === '1' && !this.adr) void this.klar.then(async () => { if ((await this.sb.auth.getSession()).data.session) await this.tilMinKlient(); });
    const ktx = { sb: this.sb, k: <T extends { data: unknown; error: unknown }>(p: PromiseLike<T>) => this.k(p), kraevKlient: () => this.kraevKlient(), funktion: <T,>(n: string, b: Record<string, unknown>) => this.funktion<T>(n, b), skift: () => this.skift(), adresse: () => this.adr } as SbKontekst;
    this.katalogDel = new SbKatalog(ktx);
    this.medlemDel = new SbMedlemmer(ktx);
    this.kontorDel = new SbKontor(ktx, this.katalogDel, this.medlemDel);
    this.profilDel = new SbProfil(ktx, this.katalogDel, () => this.b);
    this.chatDel = new SbChat(ktx);
    this.steamerDel = new SbSteamer(ktx);
    this.kundeDel = new SbKunde(ktx, this.katalogDel);
    this.kundecenterDel = new SbKundecenter(ktx, this.katalogDel, () => this.automationDel);
    this.rykkerDel = new SbRykker(ktx, this.katalogDel, () => this.kundecenterDel.mineSkabeloner());
    this.ansoegDel = new SbAnsoeg(ktx);
    this.udlaegDel = new SbUdlaeg(ktx);
    this.crmDel = new SbCrm(ktx, this.medlemDel);
    this.kortDel = new SbKort(ktx);
    this.medarbejderDel = new SbMedarbejder(ktx);
    this.brugerDel = new SbBrugere(ktx, this.medarbejderDel, async () => { await this.opdaterBruger((await this.sb.auth.getUser()).data.user); return this.b; });
    this.betalingDel = new SbBetalinger(ktx);
    this.idagDel = new SbIdag(ktx, this.katalogDel);
    this.analyseDel = new SbAnalyse(ktx);
    this.automationDel = new SbAutomation(ktx);
    this.anmeldDel = new SbAnmeld(ktx);
    this.kundeserviceDel = new SbKundeservice(ktx);
    this.marketingDel = new SbMarketing(ktx);
    this.sb.auth.onAuthStateChange((_h, s) => { void this.opdaterBruger(s?.user ?? null); });
  }
  // Live-opdateringer (Realtime): én kanal for den indloggede bruger i virksomheden – startes efter login, stoppes ved log ud
  // eller skift af virksomhed. Samme bruger og virksomhed igen (fx ny adgangsnøgle hver time) genbruger kanalen.
  private liveStop: (() => void) | null = null;
  private liveNoegle = '';
  private saetLive(bruger: string | null, k: string | null) {
    const noegle = bruger && k ? bruger + ':' + k : '';
    if (noegle === this.liveNoegle) return;
    this.liveStop?.(); this.liveStop = null; this.liveNoegle = noegle;
    if (noegle && typeof document !== 'undefined') this.liveStop = startSupabaseLive(this.sb as never, k!);
  }
  private async vekslLink(q: URLSearchParams) {
    // Support fra konsollen på admin-subdomænet: sessionen følger med i adressens # (se aabnKlient)
    if (typeof location !== 'undefined' && location.hash?.startsWith('#overtag=')) {
      try {
        const t = JSON.parse(atob(location.hash.slice(9))) as { a: string; r: string };
        history.replaceState(null, '', location.pathname + location.search + '#/admin/overblik');
        await this.sb.auth.setSession({ access_token: t.a, refresh_token: t.r });
      } catch { this.linkFejl = 'Linket fra konsollen virkede ikke. Prøv igen.'; }
      return;
    }
    const th = q.get('token_hash'), type = q.get('type');
    if (!th || !type || !['signup', 'recovery', 'invite', 'magiclink', 'email_change', 'email'].includes(type)) return;
    this.fraLink = true;
    const { error } = await this.sb.auth.verifyOtp({ token_hash: th, type: type as 'signup' });
    if (error) this.linkFejl = 'Linket er udløbet eller allerede brugt.';
    try { history.replaceState(null, '', location.pathname + location.hash); } catch { /* ok */ }   // koden må ikke ligge i adressen
  }

  // ---------- Fælles ----------
  lyt(fn: () => void) { this.lyttere.add(fn); const af = live.lyt(fn); return () => { this.lyttere.delete(fn); af(); }; }
  private skift() { this.katalogDel?.glemKatalog(); this.kontorDel?.glemStam(); this.lyttere.forEach((f) => f()); }
  /** Svar fra Supabase → data, eller en dansk fejl. Typen er den fejlfri gren af svaret. */
  private async k<R extends { data: unknown; error: unknown }>(p: PromiseLike<R>): Promise<R extends { error: null } ? R['data'] : never> {
    let r: R;
    try { r = await p; } catch (e) { throw oversaetFejl(e); }
    if (r.error) throw oversaetFejl(r.error);
    return r.data as never;
  }
  /** Klientens id (kun for indloggede med adgang – RLS). */
  private klientId(): Promise<string | null> {
    if (!this.adr) return Promise.resolve(null);
    this.kid ??= (async () => {
      const q = this.sb.from('klienter').select('id');
      const { data } = await (this.adr!.slug ? q.eq('slug', this.adr!.slug) : q.eq('domaene', this.adr!.vaert)).maybeSingle();
      if (data) return (data as { id: string }).id;
      // En kunde kan ikke læse klienter – virksomhedens id findes ud fra adressen, når hun er kunde dér
      const { data: kid } = await this.sb.rpc('mit_kunde_klient', { vaert: this.adr!.vaert, sti: this.adr!.slug });
      return (kid as string | null) ?? null;
    })();
    return this.kid;
  }
  private async kraevKlient(): Promise<string> { const k = await this.klientId(); if (!k) throw fejl('Log ind for at fortsætte.'); return k; }
  private async opdaterBruger(u: User | null) {
    this.kid = null;
    if (!u) { this.b = null; this.p = null; this.saetLive(null, null); this.skift(); return; }
    const k = await this.klientId();
    this.saetLive(u.id, k);
    if (k) {
      const { data } = await this.sb.rpc('mig_hos', { k });
      this.b = (data as Bruger | null) ?? null;
    } else this.b = null;
    const { data: pf } = await this.sb.rpc('er_platform');
    this.p = pf ? { id: u.id, navn: (u.user_metadata?.navn as string) ?? u.email ?? 'Freemi', email: u.email ?? '' } : null;
    this.skift();
  }
  private async funktion<T>(navn: string, body: Record<string, unknown>): Promise<T> {
    const { data, error } = await this.sb.functions.invoke(navn, { body });
    if (error) {
      let besked = '';
      try { besked = (await (error as { context?: Response }).context?.json())?.fejl ?? ''; } catch { /* ok */ }
      throw besked ? fejl(besked) : oversaetFejl(error);
    }
    return data as T;
  }

  // ---------- Klienten og brandet ----------
  async klient(): Promise<KlientOffentlig> {
    if (!this.adr) return PIXIE_SELV;
    const data = await this.k(this.sb.rpc('klient_offentlig', { vaert: this.adr.vaert, sti: this.adr.slug }));
    if (!data) throw fejl('Adressen findes ikke. Tjek, at den er skrevet rigtigt.');
    const d = data as KlientOffentlig;
    // Virksomheden har eget domæne, men siden er åbnet på den gamle adresse (freemi.dk/firma): send videre, så links aldrig går i stykker
    if (d.domaene && this.adr.slug && typeof location !== 'undefined' && location.pathname.split('/')[1] === this.adr.slug) {
      location.replace(`https://${d.domaene}${location.pathname.slice(this.adr.slug.length + 1) || '/'}${location.search}${location.hash}`);
    }
    return { ...d, domaene: d.domaene ?? '', brand: { ...PIXIE_BRAND, ...d.brand } };
  }
  async gemBrand(b: KlientBrand) {
    const f = tjekBrand(b); if (Object.keys(f).length) throw fejl('Tjek de markerede felter.', f);
    const k = await this.kraevKlient();
    await this.k(this.sb.from('klienter').update({ brand: b }).eq('id', k));
    this.skift();
  }

  // ---------- Login ----------
  bruger() { return this.b; }
  async logInd(email: string, kode: string) {
    if (!email.trim() || !kode) throw fejl('Skriv e-mail og adgangskode.');
    const r = await this.k(this.sb.auth.signInWithPassword({ email: email.trim().toLowerCase(), password: kode }));
    // Bekræftet, men linket blev åbnet et andet sted: gør tilmeldingen færdig nu
    if (r.user?.user_metadata?.tilmelding) return this.opretFraTilmelding(r.user);
    await this.opdaterBruger(r.user);
    if (!this.b && !this.adr && (await this.tilMinKlient())) return new Promise<Bruger>(() => {});   // siden genindlæses på virksomhedens adresse
    if (!this.b) return this.ingenAdgang('Du har ikke adgang til denne virksomhed. Tjek adressen, eller kontakt din administrator.');
    return this.b;
  }
  /** Logget ind på Freemis egen adresse: send brugeren videre til sin virksomhed (den første, hvis der er flere). */
  private async tilMinKlient(): Promise<boolean> {
    const { data: u } = await this.sb.auth.getUser();
    if (!u.user) return false;
    const { data } = await this.sb.from('klient_brugere').select('klienter(slug)').eq('bruger_id', u.user.id).eq('status', 'aktiv').order('oprettet').limit(1).maybeSingle();
    const slug = (data as { klienter: { slug: string } | null } | null)?.klienter?.slug;
    if (!slug) return false;
    location.assign(`${location.origin}/${slug}/#/login`);
    return true;
  }
  /** Engangskode på mail (koden og et link sendes i én mail). SMS kommer, når SMS-udbyderen er koblet på. */
  async sendLoginKode(til: string) {
    const em = til.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(em)) throw fejl(/^\+?[\d\s]{8,}$/.test(em) ? 'Login med kode på SMS kommer snart. Brug din e-mail.' : 'Skriv din e-mail.');
    const { error } = await this.sb.auth.signInWithOtp({ email: em, options: { shouldCreateUser: false, emailRedirectTo: `${location.origin}/bekraeft${this.adr?.slug ? `?k=${this.adr.slug}` : ''}` } });
    // Samme svar, uanset om e-mailen findes – så ingen kan se, hvem der har en konto
    if (error && !/signups not allowed|not found|user not/i.test(error.message)) throw oversaetFejl(error);
    return { sendtTil: em };
  }
  async logIndMedKode(til: string, kode: string) {
    const r = await this.k(this.sb.auth.verifyOtp({ email: til.trim().toLowerCase(), token: kode.replace(/\D/g, ''), type: 'email' }))
      .catch(() => { throw fejl('Koden er forkert eller udløbet. Bed om en ny.'); });
    await this.opdaterBruger(r.user);
    if (!this.b && !this.adr && (await this.tilMinKlient())) return new Promise<Bruger>(() => {});
    if (!this.b) return this.ingenAdgang('Du har ikke adgang til denne virksomhed. Tjek adressen, eller kontakt din administrator.');
    return this.b;
  }
  /** Kundens link fra booking-mailen (…?token_hash=…&type=magiclink#/link): linket er vekslet til et login, når siden åbner. */
  async logIndMedLink(_token: string) {
    await this.klar;
    const { data } = await this.sb.auth.getSession();
    if (!data.session || this.linkFejl) throw fejl('Linket er udløbet eller allerede brugt. Log ind med din e-mail og en engangskode i stedet.');
    await this.opdaterBruger(data.session.user);
    if (!this.b) return this.ingenAdgang('Du har ikke adgang til denne virksomhed. Tjek adressen, eller kontakt os.');
    return this.b;
  }
  /** Logget ind, men ingen adgang: log ud igen. Har kunden deaktiveret sin profil her, får hun en venlig besked (core/deaktiver.ts). */
  private async ingenAdgang(besked: string): Promise<never> {
    const k = await this.klientId();
    const { data } = k ? await this.sb.rpc('min_profil_deaktiveret', { k }) : { data: null };
    await this.sb.auth.signOut();
    const d = data as { firma: string; telefon: string } | null;
    throw fejl(d ? deaktiveretLogin(d.firma, d.telefon) : besked);
  }
  async logUd() { await this.sb.auth.signOut(); this.b = null; this.p = null; this.skift(); }
  /** Kunden deaktiverer sin profil (20261029880000_deaktiver.sql) og logges ud. */
  async deaktiverMinProfil() { await this.k(this.sb.rpc('deaktiver_min_profil', { k: await this.kraevKlient() })); await this.logUd(); }
  async glemtKode(email: string) {
    const em = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(em)) throw fejl('Tjek de markerede felter.', { email: 'Skriv en gyldig e-mail.' });
    await this.k(this.sb.auth.resetPasswordForEmail(em, { redirectTo: `${location.origin}/bekraeft${this.adr?.slug ? `?k=${this.adr.slug}` : ''}` }));
    return {};
  }
  async tjekToken(token: string) {
    await this.klar;
    if (token === 'inv') return this.brugerDel.tjekInvitation();   // invitationen fra kontoret (main.tsx: ?inv=1)
    const { data } = await this.sb.auth.getSession();
    return data.session && !this.linkFejl ? { gyldig: true, type: 'nulstil' as const, email: data.session.user.email ?? '' } : { gyldig: false, aarsag: 'Linket er udløbet eller allerede brugt. Bed om et nyt.' };
  }
  async nulstilKode(_token: string, kode: string) {
    if (kode.length < 8) throw fejl('Tjek de markerede felter.', { kode: 'Mindst 8 tegn.' });
    const r = await this.k(this.sb.auth.updateUser({ password: kode }));
    await this.opdaterBruger(r.user);
    // Konsollen (admin.freemi.dk): Freemi bliver logget ind dér; alle andre logges ud
    if (paaKonsol()) { if (!this.p) await this.logUd(); return null; }
    if (this.b) return this.b;
    if (r.user?.user_metadata?.tilmelding) return this.opretFraTilmelding(r.user);   // tilmeldt, men virksomheden er ikke oprettet endnu
    if (await this.tilMinKlient()) return new Promise<Bruger>(() => {});
    await this.logUd();
    return null;   // koden er gemt – log ind igen
  }

  // ---------- Freemis hjemmeside ----------
  async pixieHjemmeside() {
    const [r, a] = await Promise.all([
      this.k(this.sb.from('pixie_referencer').select('id, navn, url, logo, vis, raekke').order('raekke')),
      this.k(this.sb.from('pixie_anmeldelser_offentlige').select('id, navn, rolle, firma, tekst, stjerner, oprettet').order('oprettet', { ascending: false })),
    ]);
    const anm = (a as (Anmeldelse & { oprettet: string })[]).map((x) => ({ ...x, status: 'godkendt' as const, samtykke: true, kilde: 'kunde' as const, klient_slug: null }));
    return offentligHjemmeside(r as Reference[], anm);
  }
  async sendHenvendelse(h: NyHenvendelse) {
    const { felter, ren, spam } = tjekHenvendelse(h);
    if (Object.keys(felter).length) throw fejl('Tjek de markerede felter.', felter);
    if (spam) return;
    await this.k(this.sb.rpc('send_henvendelse', { p_navn: ren.navn, p_firma: ren.firma, p_email: ren.email, p_telefon: ren.telefon, p_besked: ren.besked }));
  }

  // ---------- Tilmelding ----------
  async slugLedig(slug: string) { return !!(await this.k(this.sb.rpc('slug_ledig', { s: slug }))); }
  async tilmeld(t: Tilmelding) {
    const brand = tilmeldingsBrand(t.brand?.logo ?? null);   // kun logoet er virksomhedens eget – resten er Freemis faste design
    const f = { ...tjekTilmelding(t, () => false), ...tjekBrand(brand) };
    const sf = slugFejl(t.slug); if (sf) f.slug = sf;
    if (Object.keys(f).length) throw fejl('Tjek de markerede felter.', f);
    if (!(await this.slugLedig(t.slug))) throw fejl('Tjek de markerede felter.', { slug: 'Adressen er taget. Prøv en anden.' });
    // Logoet kan være stort og hører ikke hjemme i login-oplysningerne: det gemmes i browseren og sendes med ved bekræftelsen.
    try { if (brand.logo) localStorage.setItem('pixie-tilmelding-logo', brand.logo); } catch { /* ok – logoet kan uploades bagefter */ }
    const em = t.email.trim().toLowerCase();
    await this.k(this.sb.auth.signUp({ email: em, password: t.kode, options: {
      emailRedirectTo: `${location.origin}/bekraeft`,
      data: { navn: t.navn.trim(), tilmelding: { firma: t.firma.trim(), slug: t.slug, hjemmeside: t.hjemmeside.trim(), telefon: t.telefon.trim(), domaene: (t.domaene ?? '').trim().toLowerCase(), brand: { ...brand, logo: null }, moduler: t.moduler, vilkaar: VILKAAR_VERSION } },
    } }));
    return { email: em };
  }
  /** Efter klik på linket i mailen (logget ind via koden i adressen): opret virksomheden og gør brugeren til ejer. */
  async bekraeftTilmelding(_token: string) {
    await this.klar;
    const { data } = await this.sb.auth.getSession();
    const u = data.session?.user;
    if (!u) throw fejl(`${this.linkFejl ?? 'Linket blev åbnet et andet sted end der, hvor du oprettede dig.'} Log ind med din e-mail og adgangskode – så gør vi jeres Freemi færdig.`);
    return this.opretFraTilmelding(u);
  }
  /** Opret virksomheden ud fra tilmeldingen (gemt på brugeren) og send videre til abonnementet, hvor kortet lægges ind. */
  private async opretFraTilmelding(u: User): Promise<Bruger> {
    const t = u.user_metadata?.tilmelding as { firma: string; slug: string; hjemmeside: string; telefon: string; domaene?: string; brand: KlientBrand; moduler: string[]; vilkaar: string } | undefined;
    if (!t) { if (await this.tilMinKlient()) return new Promise<Bruger>(() => {}); throw fejl('Din Freemi er allerede oprettet. Log ind på din adresse.'); }
    let logo: string | null = null;
    try { logo = localStorage.getItem('pixie-tilmelding-logo'); } catch { /* ok */ }
    await this.k(this.sb.rpc('opret_klient_selv', { p_navn: t.firma, p_slug: t.slug, p_hjemmeside: t.hjemmeside, p_brand: tilmeldingsBrand(logo), p_moduler: t.moduler, p_vilkaar: t.vilkaar,
      p_ejer_navn: u.user_metadata?.navn ?? '', p_ejer_telefon: t.telefon, p_indst: { firma: { navn: t.firma, email: u.email, telefon: t.telefon, hjemmeside: t.hjemmeside } } }));
    try { localStorage.removeItem('pixie-tilmelding-logo'); } catch { /* ok */ }
    // Eget subdomæne valgt ved tilmeldingen: læg det klar, så Freemi aktiverer det, når DNS peger rigtigt
    if (t.domaene) {
      const { data: ny } = await this.sb.from('klienter').select('id').eq('slug', t.slug).maybeSingle();
      if (ny) await this.sb.rpc('anmod_domaene', { k: (ny as { id: string }).id, d: t.domaene });   // fejl her stopper ikke oprettelsen – domænet kan sættes igen
    }
    // Standardkataloget (ydelser, priser, biltyper …). Fejler det her, lægges det ind næste gang ejeren åbner Kom i gang.
    const { data: kl } = await this.sb.from('klienter').select('id').eq('slug', t.slug).maybeSingle();
    if (kl) await this.katalogDel.saaKatalog((kl as { id: string }).id).catch(() => false);
    await this.sb.auth.updateUser({ data: { tilmelding: null } });
    location.replace(`${location.origin}/${t.slug}/#/admin/abonnement`);
    return new Promise<Bruger>(() => {});
  }
  async komIGang() {
    const k = await this.kraevKlient();
    if (!this.katalogTjekket && (this.b?.rolle === 'ejer' || this.b?.rolle === 'admin')) { this.katalogTjekket = true; if (await this.katalogDel.saaKatalog(k).catch(() => false)) this.skift(); }
    const [kl, n] = await Promise.all([
      this.k(this.sb.from('klienter').select('brand, kom_i_gang, domaene').eq('id', k).single()),
      this.k(this.sb.from('profiler').select('id', { count: 'exact', head: true }).eq('klient_id', k).neq('rolle', 'ejer')),
    ]);
    const x = kl as { brand: KlientBrand; kom_i_gang: string[]; domaene: string | null };
    return komIGang({ logo: !!x.brand?.logo, medarbejdere: (n as unknown as number) ?? 0, bookinger: 0, afkrydset: x.kom_i_gang ?? [], domaene: !!x.domaene });
  }
  async afkrydsKomIGang(id: string, klar: boolean) {
    if (!KOM_I_GANG_MANUELLE.includes(id)) throw fejl('Det trin krydses af automatisk.');
    await this.k(this.sb.rpc('kryds_kom_i_gang', { k: await this.kraevKlient(), trin: id, klar }));
  }

  // ---------- Abonnement ----------
  async abonnement(): Promise<AbonnementVisning> {
    const k = await this.kraevKlient();
    const [r, f, koeb, ejer, bet] = await Promise.all([
      this.k(this.sb.from('klienter').select('*').eq('id', k).single()),
      this.k(this.sb.from('forbrug_maaned').select('sms, mails, stoppet').eq('klient_id', k).eq('maaned', iDag().slice(0, 8) + '01').maybeSingle()),
      this.k(this.sb.from('sms_koeb').select('id, oprettet, antal, beloeb, status, auto, fejl, kvittering').eq('klient_id', k).order('oprettet', { ascending: false }).limit(12)),
      this.k(this.sb.from('profiler').select('email').eq('klient_id', k).eq('rolle', 'ejer').limit(1).maybeSingle()),
      this.k(this.sb.from('abonnement_betalinger').select('id, tid, beloeb, status, periode_start, kvittering').eq('klient_id', k).order('tid', { ascending: false }).limit(24)),
    ]);
    const kl = tilKlient(r as KlientRk);
    const st = abonnementStatus(kl);
    const fm = (f as { sms: number; mails: number; stoppet: number } | null) ?? { sms: 0, mails: 0, stoppet: 0 };
    const kb: SmsKoeb[] = (koeb as { id: number; oprettet: string; antal: number; beloeb: number; status: SmsKoeb['status']; auto: boolean; fejl: string | null; kvittering: string | null }[])
      .map((x) => ({ id: x.id, tid: x.oprettet, antal: x.antal, beloeb_inkl: x.beloeb, status: x.status, auto: x.auto, fejl: x.fejl, kvittering: x.kvittering }));
    const betalinger: AbonBetaling[] = (bet as { id: string; tid: string; beloeb: number; status: AbonBetaling['status']; periode_start: string | null; kvittering: string | null }[])
      .map((x) => ({ id: x.id, tid: x.tid, beloeb_inkl: x.beloeb, status: x.status, periode_start: x.periode_start, kvittering: x.kvittering }));
    const m = new Date().toISOString().slice(0, 7);
    return { status: st, pris_inkl: PRIS.maaned_inkl, proeve_slut: kl.proeve_slut ?? null, naeste_traek: st === 'proeve' || st === 'aktiv' ? naesteTraek(kl.proeve_slut ?? kl.oprettet) : null,
      opsagt_til: kl.opsagt_til ?? null, kort: kl.kort ?? null, ejer_email: (ejer as { email: string } | null)?.email ?? '', domaene: kl.domaene, domaene_anmodet: kl.domaene_anmodet ?? null,
      adresse: kl.domaene || `${PIXIE_VAERT}/${kl.slug}`, vilkaar_version: kl.vilkaar_version ?? null, vilkaar_tid: kl.vilkaar_tid ?? null,
      forbrug: { sms: fm.sms, mails: fm.mails, stoppet: fm.stoppet, inkluderet: inkluderet(st === 'proeve'), saldo: kl.sms_saldo ?? 0, auto: !!kl.sms_auto,
        loft_inkl: kl.sms_loft ?? SALDO.loft_standard_inkl, koebt_inkl: kb.filter((x) => x.auto && x.status !== 'fejlet' && x.tid.slice(0, 7) === m).reduce((s, x) => s + x.beloeb_inkl, 0),
        pris_sms_inkl: SALDO.sms_pris_inkl, pakker: [...SALDO.pakker], koeb: kb }, betalinger };
  }
  async startBetaling() { return this.funktion<{ url: string }>('betaling', { klient: await this.kraevKlient(), handling: 'start' }); }
  async opsigAbonnement() { await this.funktion('betaling', { klient: await this.kraevKlient(), handling: 'opsig' }); this.skift(); }
  async genoptagAbonnement() { await this.funktion('betaling', { klient: await this.kraevKlient(), handling: 'genoptag' }); this.skift(); }
  async gemSmsIndst(auto: boolean, loft_inkl: number) {
    if (!Number.isInteger(loft_inkl) || loft_inkl < 0 || loft_inkl > 500_000) throw fejl('Tjek de markerede felter.', { loft: 'Skriv et loft mellem 0 og 5.000 kr.' });
    if (auto && loft_inkl < SALDO.auto_pakke * SALDO.sms_pris_inkl) throw fejl('Tjek de markerede felter.', { loft: 'Loftet skal mindst være én pakke (69 kr.).' });
    await this.k(this.sb.rpc('gem_sms_indst', { k: await this.kraevKlient(), ekstra: auto, loft: loft_inkl }));
    this.skift();
  }
  async koebSms(antal: number) {
    if (!erPakke(antal)) throw fejl('Vælg en af pakkerne.');
    const r = await this.funktion<{ url: string }>('betaling', { klient: await this.kraevKlient(), handling: 'sms', antal });
    return { url: r.url, saldo: 0 };
  }
  async anmodDomaene(domaene: string) {
    await this.k(this.sb.rpc('anmod_domaene', { k: await this.kraevKlient(), d: domaene }));
    this.skift();
  }
  async tjekDomaene() {
    const r = await this.funktion<{ ok: boolean; domaene?: string; grund?: string }>('forbind-domaene', { klient: await this.kraevKlient() });
    if (r.ok) this.skift();
    return r;
  }
  async minAnmeldelse() {
    const k = await this.kraevKlient();
    const r = await this.k(this.sb.from('pixie_anmeldelser').select('*').eq('klient_id', k).maybeSingle());
    return r ? { ...(r as Anmeldelse), klient_slug: this.adr?.slug ?? null } : null;
  }
  async skrivAnmeldelse(a: NyAnmeldelse) {
    const { felter, ren } = tjekAnmeldelse(a);
    if (Object.keys(felter).length) throw fejl('Tjek de markerede felter.', felter);
    await this.k(this.sb.rpc('skriv_anmeldelse', { k: await this.kraevKlient(), p_navn: ren.navn, p_rolle: ren.rolle, p_tekst: ren.tekst, p_stjerner: ren.stjerner, p_samtykke: ren.samtykke }));
    this.skift();
  }

  // ---------- Freemi-konsollen ----------
  platformBruger() { return this.p; }
  async platformLogInd(email: string, kode: string) {
    const r = await this.k(this.sb.auth.signInWithPassword({ email: email.trim().toLowerCase(), password: kode }));
    await this.opdaterBruger(r.user);
    return this.kunPlatform();
  }
  async platformSendLoginKode(email: string) {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) throw fejl('Skriv din e-mail.');
    return this.sendLoginKode(email);   // linket i mailen går til konsollens /bekraeft (location.origin)
  }
  async platformLogIndMedKode(email: string, kode: string) {
    const r = await this.k(this.sb.auth.verifyOtp({ email: email.trim().toLowerCase(), token: kode.replace(/\D/g, ''), type: 'email' }))
      .catch(() => { throw fejl('Koden er forkert eller udløbet. Bed om en ny.'); });
    await this.opdaterBruger(r.user);
    return this.kunPlatform();
  }
  async platformLogIndMedLink() {
    if (!this.fraLink) return null;
    this.fraLink = false;
    await this.klar;
    const { data } = await this.sb.auth.getSession();
    if (!data.session || this.linkFejl) throw fejl('Linket er udløbet eller allerede brugt. Bed om en ny kode.');
    await this.opdaterBruger(data.session.user);
    return this.kunPlatform();
  }
  /** Kun Freemi kommer ind i konsollen. Alle andre logges ud igen med en venlig besked. */
  private async kunPlatform(): Promise<PlatformBruger> {
    if (this.p) return this.p;
    await this.logUd();
    throw fejl(KUN_FREEMI);
  }
  async platformLogUd() { await this.logUd(); }
  private kraevPlatform() { if (!this.p) throw fejl('Log ind på Freemi-platformen.'); }
  async platformKlienter(): Promise<KlientOversigt[]> {
    this.kraevPlatform();
    const m = iDag().slice(0, 8) + '01';
    const [kl, kb, fm, koeb, tal] = await Promise.all([
      this.k(this.sb.from('klienter').select('*').order('oprettet', { ascending: false })),
      this.k(this.sb.from('klient_brugere').select('klient_id, rolle, status')),
      this.k(this.sb.from('forbrug_maaned').select('klient_id, sms, rcs, mails, stoppet').eq('maaned', m)),
      this.k(this.sb.from('sms_koeb').select('klient_id, beloeb, status').gte('oprettet', m)),
      this.k(this.sb.rpc('pixie_klient_tal')),   // kunder, bookinger og ejer pr. klient (20261030110000_konsol.sql)
    ]);
    const brugere = kb as { klient_id: string; rolle: string; status: string }[];
    const pr = new Map((tal as { klient_id: string; kunder: number; bookinger: number; ejer_navn: string; ejer_email: string }[] | null ?? []).map((x) => [x.klient_id, x]));
    return (kl as KlientRk[]).map((r) => {
      const k = tilKlient(r); const st = abonnementStatus(k);
      const f = (fm as { klient_id: string; sms: number; rcs: number; mails: number; stoppet: number }[]).find((x) => x.klient_id === k.id) ?? { sms: 0, rcs: 0, mails: 0, stoppet: 0 };
      const b = (koeb as { klient_id: string; beloeb: number; status: string }[]).filter((x) => x.klient_id === k.id && x.status === 'betalt');
      const koebt = b.reduce((s, x) => s + x.beloeb, 0);
      const regn = maanedsRegnskab({ sms: f.sms, rcs: f.rcs, mails: f.mails, betalende: k.kilde === 'selv' && (st === 'aktiv' || st === 'opsagt'), koebt_inkl: koebt, koeb_antal: b.length });
      const t = pr.get(k.id);
      return { ...k, brugere: brugere.filter((x) => x.klient_id === k.id && x.status === 'aktiv' && x.rolle !== 'kunde').length,
        kunder: t?.kunder ?? 0, bookinger: t?.bookinger ?? 0, ejer: t?.ejer_email ?? '', ejer_navn: t?.ejer_navn ?? '',
        sms: f.sms, sms_stoppet: f.stoppet, sms_koebt_kr: koebt, overskud: regn.overskud, sms_kost: regn.sms_kost };
    });
  }
  /** Opret eller ret en klient (pixie_gem_klient, 20261029200100_brugere.sql). En ny klients ejer inviteres af edge-funktionen "brugere". */
  async gemKlient(k: Omit<Klient, 'id' | 'oprettet'> & { id?: string }, ejer?: { navn: string; email: string }) {
    this.kraevPlatform();
    const f: Record<string, string> = { ...tjekBrand(k.brand) };
    const slug = rensSlug(k.slug || k.navn);
    if (k.navn.trim().length < 2) f.navn = 'Skriv klientens navn.';
    if (slug.length < 2) f.slug = 'Skriv et kort navn til adressen (fx steamgreen).';
    else if (!k.id && RESERVEREDE.includes(slug)) f.slug = 'Det navn er reserveret. Vælg et andet.';
    if (k.domaene && !/^[a-z0-9-]+(\.[a-z0-9-]+){2,}$/.test(k.domaene.trim().toLowerCase())) f.domaene = 'Skriv adressen, fx booking.firma.dk – eller lad feltet stå tomt.';
    if (!k.id && (!ejer || ejer.navn.trim().length < 2 || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(ejer.email.trim()))) f.ejer = 'Skriv navn og e-mail på klientens ejer.';
    if (Object.keys(f).length) throw fejl('Tjek de markerede felter.', f);
    const r = await this.k(this.sb.rpc('pixie_gem_klient', { p: { id: k.id ?? null, navn: k.navn, slug, domaene: k.domaene ?? '', hjemmeside: k.hjemmeside ?? '', brand: k.brand,
      moduler: k.moduler, status: k.status }, p_ejer: k.id ? null : ejer })) as { id: string; ejer?: string };
    if (r.ejer) await this.funktion('brugere', { klient: r.id, profil: r.ejer, handling: 'inviter' });
    this.skift();
    return { id: r.id };
  }
  async aabnKlient(slug: string): Promise<Bruger> {
    this.kraevPlatform();
    const k = await this.k(this.sb.from('klienter').select('id').eq('slug', slug).single()) as { id: string };
    await this.k(this.sb.from('pixie_support').insert({ klient_id: k.id, bruger_id: this.p!.id, grund: 'Support fra Freemi-konsollen' }));
    if (KONSOL_VAERT) {
      // Konsollen ligger på sit eget subdomæne: login gemmes pr. adresse, så sessionen gives med til virksomhedens side (i #, sendes aldrig til en server)
      const s = (await this.sb.auth.getSession()).data.session;
      const overtag = s ? '#overtag=' + btoa(JSON.stringify({ a: s.access_token, r: s.refresh_token })) : '#/admin/overblik';
      location.assign(`https://${PIXIE_VAERT}/${slug}/${overtag}`);
    } else location.assign(`${location.origin}/${slug}/#/admin/overblik`);
    return { id: this.p!.id, navn: 'Freemi (support)', rolle: 'ejer', via_pixie: true };
  }
  /** Kun staging: alle testdata forfra (staging_nulstil i supabase/staging/staging.sql – findes ikke i produktionen). */
  async stagingNulstil(): Promise<StagingNulstillet> {
    return await this.k(this.sb.rpc('staging_nulstil')) as StagingNulstillet;
  }
  /** Kun staging: engangsnøgle fra staging-rolle → siden åbnes forfra med nøglen (vekslLink logger ind som test-brugeren), så intet
   *  fra den forrige rolle hentes med den nye – og #/login sender videre til rollens forside. */
  async stagingSkiftRolle(rolle: StagingRolle): Promise<void> {
    if (!STAGING) throw fejl('Findes kun på staging.');
    const { token_hash } = await this.funktion<{ token_hash: string }>('staging-rolle', { rolle });
    location.replace(`${location.pathname}?token_hash=${encodeURIComponent(token_hash)}&type=magiclink#/login`);
  }
  async rapporterFejl(r: FejlRapport) { await this.sb.functions.invoke('fejl', { body: r }); }
  async platformFejl(timer: number, kilde: FejlKilde | null) {
    this.kraevPlatform();
    return await this.k(this.sb.rpc('platform_fejl', { p_timer: timer, p_kilde: kilde })) as FejlGruppe[];
  }
  async platformDriftStatus() { this.kraevPlatform(); return await this.k(this.sb.rpc('platform_drift_status')) as DriftStatus; }
  async platformLatepointSynk() { this.kraevPlatform(); return await this.k(this.sb.rpc('platform_latepoint_synk')) as LatepointSynk; }
  async platformLatepointSynkNu() { this.kraevPlatform(); await this.k(this.sb.rpc('platform_latepoint_synk_nu')); }
  async aktiverDomaene(slug: string) {
    this.kraevPlatform();
    const k = await this.k(this.sb.from('klienter').select('id').eq('slug', slug).single()) as { id: string };
    const r = await this.funktion<{ ok: boolean; grund?: string }>('forbind-domaene', { klient: k.id });
    if (!r.ok) throw fejl(r.grund ?? 'Domænet kunne ikke aktiveres.');
    this.skift();
  }
  async platformHjemmeside() {
    this.kraevPlatform();
    const [r, a, h] = await Promise.all([
      this.k(this.sb.from('pixie_referencer').select('*').order('raekke')),
      this.k(this.sb.from('pixie_anmeldelser').select('*, klienter(slug)').order('oprettet', { ascending: false })),
      this.k(this.sb.from('pixie_henvendelser').select('*').order('oprettet', { ascending: false }).limit(200)),
    ]);
    const anm = (a as (Omit<Anmeldelse, 'klient_slug'> & { klienter: { slug: string } | null })[]).map(({ klienter, ...x }) => ({ ...x, klient_slug: klienter?.slug ?? null }));
    return { referencer: r as Reference[], anmeldelser: anm, henvendelser: h as Awaited<ReturnType<Api['platformHjemmeside']>>['henvendelser'] };
  }
  async gemReference(r: Omit<Reference, 'id' | 'raekke'> & { id?: string }) {
    this.kraevPlatform();
    const f = tjekReference(r); if (Object.keys(f).length) throw fejl('Tjek de markerede felter.', f);
    let logo = r.logo;
    if (logo?.startsWith('data:')) logo = await this.uploadLogo(logo);   // offentligt lager "pixie-logoer"
    const v = { navn: r.navn.trim(), url: r.url.trim(), logo, vis: r.vis };
    if (r.id) await this.k(this.sb.from('pixie_referencer').update(v).eq('id', r.id));
    else {
      const sidst = await this.k(this.sb.from('pixie_referencer').select('raekke').order('raekke', { ascending: false }).limit(1).maybeSingle()) as { raekke: number } | null;
      await this.k(this.sb.from('pixie_referencer').insert({ ...v, raekke: (sidst?.raekke ?? -1) + 1 }));
    }
    this.skift();
  }
  private async uploadLogo(dataUrl: string): Promise<string> {
    const [hoved, b64] = dataUrl.split(',');
    const mime = /data:([^;]+)/.exec(hoved)?.[1] ?? 'image/png';
    const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    if (bytes.length > GRAENSER.logo_bytes) throw fejl('Logoet må højst fylde 300 KB.');
    const sti = `${crypto.randomUUID()}.${mime === 'image/svg+xml' ? 'svg' : mime.split('/')[1]}`;
    await this.k(this.sb.storage.from('pixie-logoer').upload(sti, bytes, { contentType: mime, upsert: false }));
    return this.sb.storage.from('pixie-logoer').getPublicUrl(sti).data.publicUrl;
  }
  async sletReference(id: string) { this.kraevPlatform(); await this.k(this.sb.from('pixie_referencer').delete().eq('id', id)); this.skift(); }
  async flytReference(id: string, retning: -1 | 1) {
    this.kraevPlatform();
    const l = await this.k(this.sb.from('pixie_referencer').select('id, raekke').order('raekke')) as { id: string; raekke: number }[];
    const i = l.findIndex((x) => x.id === id), j = i + retning;
    if (i < 0 || j < 0 || j >= l.length) return;
    await Promise.all([this.sb.from('pixie_referencer').update({ raekke: j }).eq('id', l[i].id), this.sb.from('pixie_referencer').update({ raekke: i }).eq('id', l[j].id)]);
    this.skift();
  }
  async gemAnmeldelse(a: NyAnmeldelse & { firma: string; id?: string }) {
    this.kraevPlatform();
    const { felter, ren } = tjekAnmeldelse(a);
    if (a.firma.trim().length < 2) felter.firma = 'Skriv virksomhedens navn.';
    if (Object.keys(felter).length) throw fejl('Tjek de markerede felter.', felter);
    const v = { navn: ren.navn, rolle: ren.rolle, tekst: ren.tekst, stjerner: ren.stjerner, samtykke: ren.samtykke, firma: a.firma.trim() };
    if (a.id) await this.k(this.sb.from('pixie_anmeldelser').update(v).eq('id', a.id));
    else await this.k(this.sb.from('pixie_anmeldelser').insert({ ...v, status: 'godkendt', kilde: 'pixie' }));
    this.skift();
  }
  async saetAnmeldelse(id: string, status: AnmeldelseStatus) { this.kraevPlatform(); await this.k(this.sb.from('pixie_anmeldelser').update({ status }).eq('id', id)); this.skift(); }
  async markerHenvendelse(id: string, behandlet: boolean) { this.kraevPlatform(); await this.k(this.sb.from('pixie_henvendelser').update({ behandlet }).eq('id', id)); this.skift(); }
}

/** Opret drift-API'et. Dele, der ikke er koblet på endnu, svarer med en tydelig besked (aldrig forkerte data). */
export function lavSupabaseApi(url: string, noegle: string): Api {
  const a = new SupabaseApi(url, noegle);
  return new Proxy(a, {
    get(t, navn, modtager) {
      if (navn in t) { const v = Reflect.get(t, navn, modtager); return typeof v === 'function' ? v.bind(t) : v; }
      if (typeof navn === 'string') for (const del of [t.katalogDel, t.medlemDel, t.kontorDel, t.profilDel, t.chatDel, t.steamerDel, t.kundeDel, t.rykkerDel, t.ansoegDel, t.automationDel, t.anmeldDel, t.kundecenterDel, t.kundeserviceDel, t.udlaegDel, t.crmDel, t.kortDel, t.medarbejderDel, t.brugerDel, t.betalingDel, t.idagDel, t.marketingDel, t.analyseDel] as object[]) if (navn in del && !INTERNE.has(navn)) return Reflect.get(del, navn);
      if (typeof navn === 'symbol' || navn === 'then') return undefined;
      return async () => { throw fejl(IKKE_KLAR); };
    },
  }) as unknown as Api;
}
